import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Mic, Square, Loader2, ArrowRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Shell, TopBar } from "../components/Shell";
import { Textarea } from "../components/ui/textarea";
import { api } from "../lib/api";

export default function Complaint() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sid = params.get("sid");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    if (!sid) return navigate("/intake");
    api.get(`/screenings/${sid}`)
      .then((r) => setText(r.data.chief_complaint || ""))
      .catch(() => toast.error("Could not load your screening."))
      .finally(() => setLoading(false));
  }, [sid]);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", new Blob(chunksRef.current, { type: "audio/webm" }), "complaint.webm");
          const r = await api.post(`/screenings/${sid}/transcribe`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (r.data.text) setText((p) => (p ? p + " " : "") + r.data.text);
          else toast.message(r.data.error || "Didn't catch that — please type it.");
        } catch (e) {
          toast.error("Transcription failed. Please type instead.");
        } finally {
          setTranscribing(false);
        }
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error("Microphone access denied. You can type instead.");
    }
  };
  const stopRec = () => {
    if (mediaRef.current?.state !== "inactive") mediaRef.current?.stop();
    setRecording(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/screenings/${sid}/complaint`, { chief_complaint: text.trim() });
      navigate(`/mode?sid=${sid}`);
    } catch (e) {
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Shell header={<TopBar title="Describe the problem" />}>
      <div className="px-6 py-5 flex flex-col gap-5 pb-28">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground max-w-[70%]">
            Tell us what's going on, in your own words. You can type or speak.
          </p>
          <button data-testid="voice-record" onClick={recording ? stopRec : startRec} disabled={transcribing || loading}
            className={`tap flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-full ${recording ? "bg-destructive text-white pulse-soft" : "bg-accent text-primary"}`}>
            {transcribing ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Transcribing…</>) : recording ? (<><Square className="w-3.5 h-3.5" /> Stop</>) : (<><Mic className="w-3.5 h-3.5" /> Speak</>)}
          </button>
        </div>

        <Textarea data-testid="complaint-text" value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Describe your symptoms, when they started, and anything that makes them better or worse…"
          className="min-h-40 rounded-2xl resize-none text-[15px]" />

        <div data-testid="emergency-note" className="flex gap-2.5 rounded-2xl border border-[#F0B7AE] bg-[#FCEBE8] px-4 py-3 text-[12.5px] leading-relaxed text-[#9E2A16]">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <p><span className="font-semibold">Emergency red flags:</span> severe swelling, difficulty breathing or swallowing, uncontrolled bleeding, or a facial injury/trauma — please seek emergency medical or dental care immediately instead of waiting for this screening.</p>
        </div>
      </div>

      <div className="sticky bottom-0 px-6 py-4 bg-background border-t border-border">
        <button data-testid="complaint-continue" onClick={save} disabled={saving || loading}
          className="tap w-full h-14 rounded-2xl brand-gradient text-white font-semibold text-base flex items-center justify-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : (<>Continue <ArrowRight className="w-5 h-5" /></>)}
        </button>
      </div>
    </Shell>
  );
}
